// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { esc } from '../format';
import { GLOSSARY } from '../glossary';
import { openModal } from './overlays';

export function openAbout(): void {
  const glossaryHtml = Object.values(GLOSSARY)
    .map((g) => `<p><strong>${esc(g.term)}.</strong> ${esc(g.def)}</p>`)
    .join('');
  const body = `
    <p>QuakeWatch is a live monitor of every earthquake on Earth, drawn directly from
    the <a href="https://earthquake.usgs.gov/earthquakes/feed/v1.0/" target="_blank" rel="noopener">USGS
    Earthquake Hazards Program</a> real-time feed. It adds an interpretation layer on top of the raw
    data: what each magnitude means in plain language, how much energy was released, how deep the quake
    was, and which tectonic plate it sits on.</p>

    <h3>Where the data comes from</h3>
    <p>Every event here is reported by the U.S. Geological Survey, which aggregates seismic networks
    worldwide. QuakeWatch fetches the public GeoJSON summary feed straight from your browser and
    refreshes it about every minute — there is no server in between. The "updated" time in the header
    tells you how fresh the data is.</p>

    <h3>How to read it</h3>
    <p>Use the controls to choose a <strong>time window</strong> (past hour, day, week or month) and a
    <strong>minimum magnitude</strong>. "All" includes the many tiny quakes that instruments detect but
    people never feel; "Significant" shows only the handful of globally notable events. Colour means
    magnitude everywhere on the site; on the map, marker colour means depth.</p>

    <h3>Important caveats</h3>
    <p>Magnitudes and locations are <strong>preliminary</strong> and are revised as more data arrives,
    especially in the first minutes after a large quake. Small-magnitude coverage is far better in
    well-instrumented regions (USA, Japan, Europe) than in remote oceans, so apparent "activity" partly
    reflects where the sensors are. The tsunami flag marks where a tsunami <em>could</em> form — it is
    <strong>not</strong> an official warning. QuakeWatch is an educational and situational-awareness
    tool, not an emergency service; follow your local authorities in an emergency.</p>

    <h3>Glossary</h3>
    ${glossaryHtml}

    <h3>Credits</h3>
    <p>Earthquake data: U.S. Geological Survey (public domain). Tectonic plate boundaries: Peter Bird
    (2003), <em>An updated digital model of plate boundaries</em>, via the PB2002 dataset. Basemap tiles
    © CARTO / © OpenStreetMap contributors.</p>`;
  openModal('About QuakeWatch', body);
}
